import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MoreVertical, UserCheck, CheckCircle2, Pencil, User, Bot } from 'lucide-react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useConversationStore } from '@/stores/useConversationStore';
import {
  usePostApiWorkspacesWorkspaceIdConversationsConversationIdAssign,
  usePostApiWorkspacesWorkspaceIdConversationsConversationIdClose,
  usePutApiWorkspacesWorkspaceIdConversationsConversationIdTitle,
  postApiWorkspacesWorkspaceIdConversationsConversationIdRead,
  getGetApiWorkspacesWorkspaceIdConversationsQueryKey,
  getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey,
} from '@/api/generated/conversations/conversations';
import { useQueryClient } from '@tanstack/react-query';
import { MOCK_CONVERSATION_ID } from '../mockData';
import type { ConversationItem } from '@/types';
import { cn, formatTimestamp, getInitials, getCustomerDisplayName } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';

interface ConversationCardProps {
  conversation: ConversationItem;
  showStoreBadge?: boolean;
}

export const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  showStoreBadge = true,
}) => {
  const navigate = useNavigate();
  const { conversationId: urlConvId } = useParams<{ conversationId?: string }>();
  const queryClient = useQueryClient();
  const {
    activeConversationId,
    setActiveConversationId,
    setCustomerDrawerOpen,
  } = useConversationStore();
  const { activeWorkspaceId } = useWorkspaceStore();

  const [isTitleDialogOpen, setIsTitleDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(conversation.title || '');
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const isActive =
    (urlConvId && urlConvId === conversation.id) ||
    activeConversationId === conversation.id;

  const assignMutation = usePostApiWorkspacesWorkspaceIdConversationsConversationIdAssign({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
      },
    },
  });

  const closeMutation = usePostApiWorkspacesWorkspaceIdConversationsConversationIdClose({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
      },
    },
  });

  const updateTitleMutation = usePutApiWorkspacesWorkspaceIdConversationsConversationIdTitle({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId!) });
        queryClient.invalidateQueries({
          queryKey: getGetApiWorkspacesWorkspaceIdConversationsConversationIdQueryKey(activeWorkspaceId!, conversation.id),
        });
        setIsTitleDialogOpen(false);
      },
    },
  });

  const handleCardClick = () => {
    setActiveConversationId(conversation.id);
    if (conversation.id !== MOCK_CONVERSATION_ID && conversation.unreadCount && conversation.unreadCount > 0 && activeWorkspaceId) {
      postApiWorkspacesWorkspaceIdConversationsConversationIdRead(activeWorkspaceId, conversation.id).catch(() => {});
      queryClient.setQueriesData<ConversationItem[]>(
        { queryKey: getGetApiWorkspacesWorkspaceIdConversationsQueryKey(activeWorkspaceId) },
        (oldConversations) => {
          if (!Array.isArray(oldConversations)) return oldConversations;
          return oldConversations.map((conv) => {
            if (conv.id === conversation.id) {
              return {
                ...conv,
                unreadCount: 0,
              };
            }
            return conv;
          });
        }
      );
    }
    navigate(`/conversations/${conversation.id}`);
  };

  const handleAssign = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (conversation.id === MOCK_CONVERSATION_ID || !activeWorkspaceId) return;
    assignMutation.mutate({ workspaceId: activeWorkspaceId, conversationId: conversation.id });
  };

  const handleCloseConfirm = () => {
    if (conversation.id === MOCK_CONVERSATION_ID || !activeWorkspaceId) return;
    closeMutation.mutate({ workspaceId: activeWorkspaceId, conversationId: conversation.id });
  };

  const handleSaveTitle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || isClosed || conversation.id === MOCK_CONVERSATION_ID || !activeWorkspaceId) return;
    updateTitleMutation.mutate({
      workspaceId: activeWorkspaceId,
      conversationId: conversation.id,
      data: { title: newTitle.trim() },
    });
  };

  const statusStr = String(conversation.status).toUpperCase();
  const isAI = statusStr === 'ACTIVE' || statusStr === 'AI_HANDLING' || conversation.status === 0;
  const isClosed = statusStr === 'CLOSED' || statusStr === 'RESOLVED' || conversation.status === 3;
  const isWaiting = statusStr === 'WAITINGFORHUMAN' || statusStr === 'WAITING' || conversation.status === 1;

  const customerDisplayName = getCustomerDisplayName(
    conversation.customerName,
    conversation.customerEmail
  );

  const initials = getInitials(
    conversation.customerName,
    conversation.customerEmail
  );

  const lastMessageObj =
    typeof conversation.lastMessage === 'object' && conversation.lastMessage !== null
      ? conversation.lastMessage
      : null;

  const lastMessageText =
    lastMessageObj?.content ??
    (typeof conversation.lastMessage === 'string' ? conversation.lastMessage : null);

  const lastMessagePreview =
    lastMessageText || 'Nenhuma mensagem nesta conversa ainda';

  const displayTimestamp =
    lastMessageObj?.createdAtUtc ||
    conversation.lastMessageAt ||
    conversation.updatedAtUtc ||
    conversation.createdAtUtc ||
    conversation.startedAt;

  return (
    <>
      <div
        id={conversation.id === MOCK_CONVERSATION_ID ? 'tour-conversation-card' : undefined}
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        className={cn(
          'group relative p-3.5 border-b border-border/70 cursor-pointer transition-all duration-150 text-left outline-none',
          'hover:bg-accent/50 focus-visible:bg-accent/50',
          isActive
            ? 'bg-accent/80 text-accent-foreground border-l-4 border-l-primary shadow-sm'
            : 'bg-card text-foreground'
        )}
      >
        <div className="flex items-start gap-3">
          {/* Customer Avatar with Initials */}
          <div className="relative shrink-0 mt-0.5">
            <Avatar className="h-10 w-10 border border-border">
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            {/* Status dot indicator */}
            {isAI && (
              <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-violet-600 text-white ring-2 ring-background">
                <Bot className="h-2 w-2" />
              </span>
            )}
            {isWaiting && (
              <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-amber-500 ring-2 ring-background animate-pulse" />
            )}
          </div>

          {/* Main Info */}
          <div className="flex-1 min-w-0">
            {/* Header: Name and Relative Time */}
            <div className="flex items-center justify-between gap-1 mb-0.5">
              <span className="font-semibold text-xs truncate text-foreground">
                {customerDisplayName}
              </span>
              <span className="text-[10px] text-muted-foreground shrink-0 font-medium">
                {formatTimestamp(displayTimestamp)}
              </span>
            </div>

            {/* Context Title */}
            {conversation.title && (
              <p className="text-[11px] font-medium text-foreground/80 truncate mb-1">
                {conversation.title}
              </p>
            )}

            {/* Last message snippet preview */}
            <p className="text-xs text-muted-foreground truncate mb-2 leading-relaxed" title={lastMessagePreview}>
              {lastMessagePreview}
            </p>

            {/* Footer Badges & Actions */}
            <div className="flex items-center justify-between gap-1.5 pt-0.5">
              <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                {/* Status Badge */}
                <StatusBadge status={String(conversation.status)} className="text-[10px] px-1.5 py-0" />

                {conversation.id === MOCK_CONVERSATION_ID && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-violet-300 dark:border-violet-800 text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/40">
                    Exemplo
                  </Badge>
                )}

                {/* Assigned Agent Badge */}
                {conversation.assignedUserName && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.2 rounded font-medium bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200/50 dark:border-blue-800/50 truncate max-w-[120px]">
                    <UserCheck className="h-2.5 w-2.5 shrink-0" />
                    <span className="truncate">{conversation.assignedUserName}</span>
                  </span>
                )}

                {/* Store Context Badge */}
                {showStoreBadge && conversation.storeName && (
                  <span className="text-[10px] px-1.5 py-0.2 rounded font-medium bg-secondary text-secondary-foreground truncate max-w-[110px]">
                    {conversation.storeName}
                  </span>
                )}
              </div>

              {/* Unread count badge & Action dropdown */}
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                {Boolean(conversation.unreadCount && conversation.unreadCount > 0) && (
                  <Badge variant="destructive" className="h-4 min-w-[16px] px-1 text-[10px] font-bold rounded-full flex items-center justify-center">
                    {conversation.unreadCount}
                  </Badge>
                )}

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-foreground opacity-70 group-hover:opacity-100"
                      aria-label="Conversation actions"
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 text-xs">
                    {(isAI || isWaiting) && (
                      <DropdownMenuItem onClick={handleAssign}>
                        <UserCheck className="h-3.5 w-3.5 mr-2 text-primary" />
                        <span>{isAI ? 'Assumir da IA' : 'Atribuir a Mim'}</span>
                      </DropdownMenuItem>
                    )}
                    {!isClosed && (
                      <DropdownMenuItem
                        onClick={() => {
                          setNewTitle(conversation.title || '');
                          setIsTitleDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-2" />
                        <span>Editar Título</span>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => {
                        setActiveConversationId(conversation.id);
                        navigate(`/conversations/${conversation.id}`);
                        setCustomerDrawerOpen(true);
                      }}
                    >
                      <User className="h-3.5 w-3.5 mr-2" />
                      <span>Ver Perfil do Cliente</span>
                    </DropdownMenuItem>
                    {!isClosed && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setIsCloseDialogOpen(true)}
                          className="text-destructive focus:text-destructive"
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-2" />
                          <span>Fechar Conversa</span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Title Dialog */}
      <Dialog open={isTitleDialogOpen} onOpenChange={setIsTitleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleSaveTitle}>
            <DialogHeader>
              <DialogTitle className="text-base font-semibold">Editar Título da Conversa</DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Defina um tópico descritivo para esta conversa.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="ex: Solicitação de Devolução #1042"
                className="text-xs"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsTitleDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!newTitle.trim() || updateTitleMutation.isPending}
              >
                {updateTitleMutation.isPending ? 'Salvando...' : 'Salvar Título'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Close Conversation Confirmation */}
      <ConfirmDialog
        open={isCloseDialogOpen}
        onOpenChange={setIsCloseDialogOpen}
        title="Fechar Conversa"
        description="Tem certeza que deseja resolver e fechar esta conversa? O chat do cliente será bloqueado."
        confirmLabel="Fechar e Resolver"
        variant="destructive"
        onConfirm={handleCloseConfirm}
      />
    </>
  );
};

export default ConversationCard;
